import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export default function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState(null);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    if (user === false) navigate("/auth");
    if (user && user.role !== "admin") navigate("/account");
  }, [user, navigate]);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    (async () => {
      try {
        const [enq, pay] = await Promise.all([api.get("/enquiries"), api.get("/payments")]);
        setEnquiries(enq.data);
        setPayments(pay.data);
      } catch {
        setEnquiries([]);
      }
    })();
  }, [user]);

  if (!user || user.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <Loader2 size={28} className="animate-spin text-[#d4af37]" />
      </div>
    );
  }

  const paidCount = (enquiries || []).filter((e) => e.deposit_status === "paid").length;

  return (
    <div className="min-h-screen px-6 pt-36 pb-24" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-[#d4af37] font-medium mb-4 flex items-center gap-2">
              <LayoutDashboard size={14} /> Admin
            </p>
            <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight text-slate-50">
              Booking <span className="italic text-[#d4af37]">dashboard.</span>
            </h1>
          </div>
          <button
            data-testid="admin-logout-button"
            onClick={async () => { await logout(); navigate("/"); }}
            className="inline-flex items-center gap-2 border border-[#d4af37]/40 text-slate-300 px-5 py-3 text-xs tracking-[0.15em] uppercase hover:border-[#d4af37] hover:text-[#d4af37] transition-colors"
          >
            <LogOut size={14} /> Sign Out
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#d4af37]/15 border border-[#d4af37]/15 mb-12" data-testid="admin-stats">
          {[
            { label: "Total Enquiries", value: enquiries?.length ?? "…" },
            { label: "Advances Paid", value: enquiries ? paidCount : "…" },
            { label: "Deposit Collected", value: `₹${(payments.filter((p) => p.status === "paid").reduce((s, p) => s + p.amount, 0) / 100).toLocaleString("en-IN")}` },
          ].map((s) => (
            <div key={s.label} className="bg-[#101C3E]/60 p-6">
              <p className="font-display text-3xl text-[#d4af37]">{s.value}</p>
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mt-2">{s.label}</p>
            </div>
          ))}
        </div>

        {enquiries === null ? (
          <div className="flex justify-center py-16"><Loader2 size={26} className="animate-spin text-[#d4af37]" /></div>
        ) : enquiries.length === 0 ? (
          <p className="text-slate-400 text-center py-16 border border-[#d4af37]/20" data-testid="admin-empty">
            No booking enquiries yet.
          </p>
        ) : (
          <div className="overflow-x-auto border border-[#d4af37]/20" data-testid="admin-enquiries-table">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="border-b border-[#d4af37]/25 text-left">
                  {["Date", "Name", "Phone", "Destination", "Travel Date", "Message", "Deposit"].map((h) => (
                    <th key={h} className="px-5 py-4 text-xs uppercase tracking-[0.2em] text-[#d4af37] font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {enquiries.map((e) => (
                  <tr key={e.id} className="border-b border-[#d4af37]/10 hover:bg-[#101C3E]/50 transition-colors" data-testid={`admin-row-${e.id}`}>
                    <td className="px-5 py-4 text-slate-400 whitespace-nowrap">
                      {new Date(e.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                    </td>
                    <td className="px-5 py-4 text-slate-50">{e.name}</td>
                    <td className="px-5 py-4 text-slate-300 whitespace-nowrap">{e.phone}</td>
                    <td className="px-5 py-4 text-slate-300">{e.destination || "—"}</td>
                    <td className="px-5 py-4 text-slate-300 whitespace-nowrap">{e.travel_date || "—"}</td>
                    <td className="px-5 py-4 text-slate-400 max-w-[220px] truncate">{e.message || "—"}</td>
                    <td className="px-5 py-4">
                      {e.deposit_status === "paid" ? (
                        <span className="text-[#d4af37] text-xs uppercase tracking-[0.15em]">Paid</span>
                      ) : (
                        <span className="text-slate-400 text-xs uppercase tracking-[0.15em]">Pending</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
